// filepath: /src/components/Formularios/InstitutionFormModal.js
import React, { useState, useEffect } from 'react';
import './InstitutionFormModal.css';
import Select2 from '../Select2';
import PrivacyNoticeModal from '../PrivacyNoticeModal';
import { useRegistration } from '../../hooks/useRegistration';

const InstitutionFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onBack,
  selectedPlan, 
  formData, 
  handleInputChange,
  stepInfo = { current: 2, total: 3, stepName: "Datos de Institución" }
}) => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const { entidades, municipios, loadingMunicipios, loadMunicipios, isLoading } = useRegistration();

  // Load municipalities when modal opens if state is already selected
  useEffect(() => {
    if (isOpen && formData.billingState) {
      loadMunicipios(formData.billingState);
    }
  }, [isOpen, formData.billingState, loadMunicipios]);

  if (!isOpen) return null;

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Actualizar formData con el archivo
      const syntheticEvent = {
        target: {
          name: 'institutionLogo',
          value: file,
          type: 'file'
        }
      };
      handleInputChange(syntheticEvent);
    }
  };

  const mexicanStates = [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
    'Chiapas', 'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima',
    'Durango', 'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo',
    'Jalisco', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
    'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
    'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content institution-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title">
            <h2>Registro de Institución</h2>
            <div className="step-indicator">
              <span className="step-text">Paso {stepInfo.current} de {stepInfo.total}</span>
              <span className="step-name">{stepInfo.stepName}</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={onSubmit} className="institution-form">
            
            {/* Sección Institución */}
            <div className="form-section">
              <h4>Institución</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="institutionRFC">RFC *</label>
                  <input
                    type="text"
                    id="institutionRFC"
                    name="institutionRFC"
                    value={formData.institutionRFC || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="RFC"
                    maxLength="13"
                    pattern="[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}"
                    title="Formato: AAA000000AAA"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="institutionName">Nombre *</label>
                  <input
                    type="text"
                    id="institutionName"
                    name="institutionName"
                    value={formData.institutionName || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Nombre de la institución"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="institutionSocialReason">Razón Social *</label>
                  <input
                    type="text"
                    id="institutionSocialReason"
                    name="institutionSocialReason"
                    value={formData.institutionSocialReason || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Razón social completa"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="institutionAbbreviation">Abreviatura *</label>
                  <input
                    type="text"
                    id="institutionAbbreviation"
                    name="institutionAbbreviation"
                    value={formData.institutionAbbreviation || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Abreviatura"
                    maxLength="10"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="institutionType">Tipo de Institución *</label>
                  <select
                    id="institutionType"
                    name="institutionType"
                    value={formData.institutionType || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Tipo de Institución *</option>
                    <option value="1 - Consulta Externa">1 - Consulta Externa</option>
                    <option value="2 - Hospital General">2 - Hospital General</option>
                    <option value="3 - Hospital Especializado">3 - Hospital Especializado</option>
                    <option value="4 - Clínica">4 - Clínica</option>
                    <option value="5 - Centro de Salud">5 - Centro de Salud</option>
                    <option value="6 - Universidad">6 - Universidad</option>
                    <option value="7 - Laboratorio">7 - Laboratorio</option>
                    <option value="8 - Farmacia">8 - Farmacia</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Sección Domicilio de Facturación */}
            <div className="form-section">
              <h4>Domicilio de Facturación</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="billingCountry">País *</label>
                  <select
                    id="billingCountry"
                    name="billingCountry"
                    value={formData.billingCountry || '52 - MX - Mexico'}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="52 - MX - Mexico">52 - MX - Mexico</option>
                    <option value="1 - US - United States">1 - US - United States</option>
                    <option value="1 - CA - Canada">1 - CA - Canada</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="billingState">Estado *</label>
                  <Select2
                    name="billingState"
                    options={entidades}
                    value={formData.billingState}                    onChange={(value) => {
                      const previousState = formData.billingState;
                      const isReselecting = previousState === value;
                      
                      // Update state value
                      const syntheticEvent = {
                        target: {
                          name: 'billingState',
                          value: value || '',
                          type: 'select'
                        }
                      };
                      handleInputChange(syntheticEvent);
                      
                      // Clear municipality when state changes and load new municipalities
                      if (formData.billingMunicipality) {
                        const clearMunicipalityEvent = {
                          target: {
                            name: 'billingMunicipality',
                            value: '',
                            type: 'select'
                          }
                        };
                        handleInputChange(clearMunicipalityEvent);
                      }
                      
                      // Load municipalities for the selected state
                      // Force refresh if re-selecting the same state
                      if (value) {
                        loadMunicipios(value, isReselecting);
                      }
                    }}
                    placeholder="Selecciona el estado"
                    isLoading={isLoading}
                    isSearchable={true}
                    isClearable={false}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingMunicipality">Ciudad *</label>
                  <Select2
                    name="billingMunicipality"
                    options={municipios}
                    value={formData.billingMunicipality}
                    onChange={(value) => {
                      const syntheticEvent = {
                        target: {
                          name: 'billingMunicipality',
                          value: value || '',
                          type: 'select'
                        }
                      };
                      handleInputChange(syntheticEvent);
                    }}
                    placeholder={
                      !formData.billingState 
                        ? 'Primero selecciona un estado' 
                        : loadingMunicipios 
                        ? 'Cargando municipios...' 
                        : municipios.length === 0 
                        ? 'No hay municipios disponibles' 
                        : 'Selecciona el municipio'
                    }
                    isLoading={loadingMunicipios}
                    isSearchable={true}
                    isClearable={false}
                    isDisabled={!formData.billingState}
                    noOptionsMessage={() => 
                      !formData.billingState 
                        ? 'Primero selecciona un estado' 
                        : 'No hay municipios disponibles para este estado'
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingPostalCode">Código Postal *</label>
                  <input
                    type="text"
                    id="billingPostalCode"
                    name="billingPostalCode"
                    value={formData.billingPostalCode || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="12345"
                    pattern="[0-9]{5}"
                    maxLength="5"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingNeighborhood">Colonia *</label>
                  <input
                    type="text"
                    id="billingNeighborhood"
                    name="billingNeighborhood"
                    value={formData.billingNeighborhood || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Colonia"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingStreet">Calle *</label>
                  <input
                    type="text"
                    id="billingStreet"
                    name="billingStreet"
                    value={formData.billingStreet || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Calle"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingExteriorNumber">Número Exterior *</label>
                  <input
                    type="text"
                    id="billingExteriorNumber"
                    name="billingExteriorNumber"
                    value={formData.billingExteriorNumber || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="12345"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingInteriorNumber">Número Interior</label>
                  <input
                    type="text"
                    id="billingInteriorNumber"
                    name="billingInteriorNumber"
                    value={formData.billingInteriorNumber || ''}
                    onChange={handleInputChange}
                    placeholder="54321"
                  />
                </div>
              </div>
            </div>

            {/* Sección Contacto */}
            <div className="form-section">
              <h4>Contacto</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="institutionPhone">Teléfono *</label>
                  <input
                    type="tel"
                    id="institutionPhone"
                    name="institutionPhone"
                    value={formData.institutionPhone || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="(123) 1-23-12-31"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="institutionEmail">Correo *</label>
                  <input
                    type="email"
                    id="institutionEmail"
                    name="institutionEmail"
                    value={formData.institutionEmail || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="contacto@institucion.com"
                  />
                </div>
              </div>
            </div>

            {/* Sección Logo */}
            <div className="form-section">
              <h4>Logo</h4>
              <div className="logo-upload-section">
                <div className="logo-upload-area">
                  {logoPreview ? (
                    <div className="logo-preview">
                      <img src={logoPreview} alt="Logo preview" />
                      <button type="button" className="remove-logo" onClick={() => {
                        setLogoPreview(null);
                        setLogoFile(null);
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="logoUpload" className="logo-upload-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Subir Logo</span>
                      <small>PNG, JPG, SVG (max 2MB)</small>
                    </label>
                  )}
                  <input
                    type="file"
                    id="logoUpload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            {/* Términos y Copyright */}
            <div className="form-section">
              <div className="terms-section">
                <div className="copyright-notice">
                  © Copyright 2025 - Think Health S.A.P.I - Todos los derechos reservados
                </div>
                
                <div className="terms-checkbox">
                  {/* <label className="checkbox-container"> */}
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      name="acceptPrivacyPolicy"
                      checked={formData.acceptPrivacyPolicy || false}
                      onChange={handleInputChange}
                      disabled={!formData.acceptPrivacyPolicy}
                      required
                    />
                    {/* <span className="checkmark"></span> */}
                    <span className="">&nbsp;&nbsp;</span>

                    He leído y acepto el 
                    <button
                      type="button"
                      className="link-button" 
                      onClick={() => setIsPrivacyModalOpen(true)}
                    >
                      aviso de privacidad
                    </button>*
                  </label>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="glass-button secondary" onClick={onBack}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Atrás
              </button>
              <button type="submit" className="glass-button primary">
                Continuar al Pago
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <PrivacyNoticeModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        onAccept={() => {
          // Auto-marcar el checkbox cuando acepten
          const syntheticEvent = {
            target: {
              name: 'acceptPrivacyPolicy',
              type: 'checkbox',
              checked: true
            }
          };
          handleInputChange(syntheticEvent);
          // Cerrar el modal después de un pequeño delay para asegurar que el estado se actualice
          setTimeout(() => {
            setIsPrivacyModalOpen(false);
          }, 100);
        }}
        institution={formData.institutionName || ''}
        showAcceptButton={true}
      />
    </div>
  );
};

export default InstitutionFormModal;
